// ─────────────────────────────────────────────────────────────────────────────
// dissolve_anims.ts — Global stroke dissolve animations
//
// Two dissolve effects for rejected / protected strokes:
//
//   RED FLASH — stroke flashes red then fades away quickly.
//               Used for invalid gestures in selection areas.
//
//   SMOKE DISSOLVE — stroke fragments into swirling dark particles that
//                    drift and fade like black smoke.
//                    Used for magically protected regions.
//
// Both effects operate on committed strokes via the InkLayer.  The caller
// provides the stroke points; this module manages the particle/animation
// state and renders overlays each frame.
// ─────────────────────────────────────────────────────────────────────────────

// ── Types ────────────────────────────────────────────────────────────────────

export type DissolveKind = 'red-flash' | 'smoke' | 'fade' | 'wind-leaf' | 'wind-sigil' | 'wind-aoe' | 'wind-resist';

interface DissolveEntry {
  kind: DissolveKind;
  startedAt: number;
  /** Stroke points in absolute canvas coordinates. */
  points: Array<{ x: number; y: number }>;
  /** Pre-computed particles (for smoke). */
  particles?: SmokeParticle[];
  /** Per-stroke fly direction (unit vector) for wind-leaf. */
  windDx?: number;
  windDy?: number;
  /** Tumble speed in rad/s for wind-leaf. */
  rotSpeed?: number;
  /** Centroid of the stroke (for wind-leaf / wind-sigil pivot). */
  cx?: number;
  cy?: number;
  /** Per-leaf phase (0..2π) — desyncs wiggle so leaves don't move in lockstep. */
  phase?: number;
  /** wind-aoe: cone parameters (in absolute canvas coords). */
  cone?: { apexX: number; apexY: number; dirX: number; dirY: number; length: number; halfAngle: number };
}

interface SmokeParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  /** 0–1 progress along lifetime */
  life: number;
  seed: number;
}

// ── Timing constants ─────────────────────────────────────────────────────────

const RED_FLASH_PEAK_MS  = 80;   // time to reach peak red
const RED_FLASH_HOLD_MS  = 60;   // hold at peak before fading
const RED_FLASH_FADE_MS  = 250;  // fade to invisible
const RED_FLASH_TOTAL_MS = RED_FLASH_PEAK_MS + RED_FLASH_HOLD_MS + RED_FLASH_FADE_MS;

const SMOKE_SPAWN_MS   = 120;   // time during which particles spawn
const SMOKE_DRIFT_MS   = 600;   // particles drift and fade after spawn
const SMOKE_TOTAL_MS   = SMOKE_SPAWN_MS + SMOKE_DRIFT_MS;
const PARTICLES_PER_PX = 0.3;   // particle density along stroke length

const FADE_TOTAL_MS = 320;       // simple alpha fade-out for dialog/region strokes

export const WIND_LEAF_TOTAL_MS = 1600;
const WIND_LEAF_DRIFT_PX_PER_S = 380;   // characteristic settled fly speed
const WIND_LEAF_WIGGLE_PX     = 14;     // perpendicular oscillation amplitude

export const WIND_SIGIL_TOTAL_MS = 750;
export const WIND_AOE_TOTAL_MS    = 600;
export const WIND_RESIST_TOTAL_MS = 700;

// ── Easing ───────────────────────────────────────────────────────────────────

function easeOutCubic(t: number): number { return 1 - (1 - t) ** 3; }
function easeInQuad(t: number): number { return t * t; }

// ── Seeded RNG (Mulberry32) ──────────────────────────────────────────────────

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s += 0x6d2b79f5;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 0xffffffff;
  };
}

// ── Dissolve Manager ─────────────────────────────────────────────────────────

export class DissolveManager {
  private _active: DissolveEntry[] = [];

  /** True if any dissolve animation is in flight. */
  get animating(): boolean { return this._active.length > 0; }

  /**
   * Start a dissolve animation for the given stroke points.
   * Points should be in absolute canvas coordinates (already offset).
   */
  add(
    kind: DissolveKind,
    points: Array<{ x: number; y: number }>,
    opts?: {
      windDx?: number; windDy?: number; rotSpeed?: number;
      /** wind-aoe: full cone description in absolute canvas coords. */
      cone?: { apexX: number; apexY: number; dirX: number; dirY: number; length: number; halfAngle: number };
    },
  ): void {
    const entry: DissolveEntry = {
      kind,
      startedAt: performance.now(),
      points: points.slice(),  // defensive copy
    };

    if (kind === 'smoke') {
      entry.particles = this._spawnSmokeParticles(points);
    }
    if ((kind === 'wind-leaf' || kind === 'wind-sigil' || kind === 'wind-resist') && opts) {
      entry.windDx = opts.windDx;
      entry.windDy = opts.windDy;
      entry.rotSpeed = opts.rotSpeed;
      let cx = 0, cy = 0;
      for (const p of points) { cx += p.x; cy += p.y; }
      entry.cx = points.length ? cx / points.length : 0;
      entry.cy = points.length ? cy / points.length : 0;
      entry.phase = Math.random() * Math.PI * 2;
    }
    if (kind === 'wind-aoe' && opts?.cone) {
      entry.cone = opts.cone;
    }

    this._active.push(entry);
  }

  /** Draw all active dissolve animations.  Call every frame. */
  draw(ctx: CanvasRenderingContext2D): void {
    const now = performance.now();
    this._active = this._active.filter(entry => {
      const elapsed = now - entry.startedAt;
      if (entry.kind === 'red-flash') {
        return this._drawRedFlash(ctx, entry, elapsed);
      } else if (entry.kind === 'fade') {
        return this._drawFade(ctx, entry, elapsed);
      } else if (entry.kind === 'wind-leaf') {
        return this._drawWindLeaf(ctx, entry, elapsed);
      } else if (entry.kind === 'wind-sigil') {
        return this._drawWindSigil(ctx, entry, elapsed);
      } else if (entry.kind === 'wind-aoe') {
        return this._drawWindAoe(ctx, entry, elapsed);
      } else if (entry.kind === 'wind-resist') {
        return this._drawWindResist(ctx, entry, elapsed);
      } else {
        return this._drawSmoke(ctx, entry, elapsed);
      }
    });
  }

  // ── Wind-leaf ──────────────────────────────────────────────────────────
  // The stroke flies away along (windDx, windDy) with non-uniform speed
  // (eases in for ~25% of the lifetime, settles to the characteristic drift
  // speed for the rest), wiggles perpendicular to the wind direction with a
  // per-leaf phase so leaves don't move in lockstep, tumbles around its
  // centroid, and casts a soft shadow beneath.  Fades to invisible.

  private _drawWindLeaf(
    ctx: CanvasRenderingContext2D,
    entry: DissolveEntry,
    elapsed: number,
  ): boolean {
    if (elapsed >= WIND_LEAF_TOTAL_MS) return false;
    const t = elapsed / WIND_LEAF_TOTAL_MS;          // 0..1

    // Speed profile: ease-in to ~25% then constant.
    //   For 0 ≤ t ≤ 0.25: integral of velocity = 0.5 * t² / 0.25 ≈ slow ramp
    //   For t > 0.25:    constant settled velocity, integral = 0.125 + (t-0.25)
    // We compute the *displacement fraction* directly (more stable than
    // numerically integrating each frame).
    const RAMP = 0.25;
    const driftFrac =
      t <= RAMP
        ? (t * t) / (2 * RAMP)                              // 0 → RAMP/2
        : (RAMP / 2) + (t - RAMP);                          // linear after
    // Total fly distance over the full lifetime
    const fullDist = WIND_LEAF_DRIFT_PX_PER_S * (WIND_LEAF_TOTAL_MS / 1000);
    const flyDist = fullDist * driftFrac;
    const dirDx = entry.windDx ?? 1;
    const dirDy = entry.windDy ?? 0;
    let dx = dirDx * flyDist;
    let dy = dirDy * flyDist;

    // Wiggle: perpendicular oscillation that grows over time (eddies / curl).
    // Amplitude ramps from 0 at start to WIGGLE_PX by t=0.6, then holds.
    const phase = entry.phase ?? 0;
    const wiggleAmp = Math.min(1, t / 0.6) * WIND_LEAF_WIGGLE_PX;
    const wiggle = Math.sin(elapsed * 0.006 + phase) * wiggleAmp;
    // Perpendicular to wind direction (rotated 90° CCW)
    const perpX = -dirDy, perpY = dirDx;
    dx += perpX * wiggle;
    dy += perpY * wiggle;

    // Tumble around centroid
    const rot = (entry.rotSpeed ?? 0) * (elapsed / 1000)
              + Math.sin(elapsed * 0.009 + phase) * 0.15;   // small jitter

    // Fade: ease-out so leaves stay visible most of the trip
    const alpha = Math.max(0, 1 - Math.pow(t, 1.5));

    const pts = entry.points;
    if (pts.length < 1) return true;

    const cx = entry.cx ?? pts[0].x;
    const cy = entry.cy ?? pts[0].y;

    // Helper to stroke the leaf path with the current ctx transform.
    const strokeLeaf = (color: string, lineWidth: number) => {
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      if (pts.length === 1) {
        ctx.arc(pts[0].x, pts[0].y, lineWidth * 0.5, 0, Math.PI * 2);
      } else {
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      }
      ctx.stroke();
    };

    // ── Shadow (drawn first, beneath) ─────────────────────────────────────
    // Offset down-right slightly, blurred, low-alpha.  Cheap version of a
    // depth cue — when the proper astral / screen-space layer lands this
    // can be replaced by a real height-based shadow.
    ctx.save();
    ctx.globalAlpha = alpha * 0.30;
    ctx.translate(dx + 6, dy + 8);
    ctx.translate(cx, cy);
    ctx.rotate(rot);
    ctx.translate(-cx, -cy);
    ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
    ctx.shadowBlur = 6;
    strokeLeaf('rgba(0, 0, 0, 0.45)', 2.4);
    ctx.shadowBlur = 0;
    ctx.restore();

    // ── Leaf (the inked stroke itself) ────────────────────────────────────
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(dx, dy);
    ctx.translate(cx, cy);
    ctx.rotate(rot);
    ctx.translate(-cx, -cy);
    strokeLeaf('rgba(50, 35, 15, 0.92)', 1.4);
    ctx.restore();

    return true;
  }

  // ── Wind sigil afterglow ──────────────────────────────────────────────────
  // The cast stroke (the wind glyph itself) lingers briefly after the wand
  // resolves, animated with a gentle wiggle and rendered in a cool
  // white-blue ink, before fading out.  Provides feedback that the cast
  // landed and pairs visually with the leaves blowing off.

  private _drawWindSigil(
    ctx: CanvasRenderingContext2D,
    entry: DissolveEntry,
    elapsed: number,
  ): boolean {
    if (elapsed >= WIND_SIGIL_TOTAL_MS) return false;
    const t = elapsed / WIND_SIGIL_TOTAL_MS;
    const pts = entry.points;
    if (pts.length < 2) return false;

    // Alpha: hold bright for ~50%, then ease out
    const alpha = t < 0.5 ? 1 : Math.max(0, 1 - (t - 0.5) / 0.5);

    // Gentle wiggle: each point oscillates perpendicular to the local
    // tangent.  A single shared phase + slow frequency reads as the glyph
    // "breathing" rather than chaotic vibration.
    const wiggleAmp = Math.sin(t * Math.PI) * 1.6;       // 0 → ~1.6 → 0
    const wigglePhase = elapsed * 0.012;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = 'rgba(220, 235, 255, 0.92)';        // cool white-blue
    ctx.shadowColor = 'rgba(150, 200, 255, 0.8)';
    ctx.shadowBlur  = 8;
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    for (let i = 0; i < pts.length; i++) {
      // Perpendicular to local tangent (between i-1 and i+1)
      const prev = pts[Math.max(0, i - 1)];
      const next = pts[Math.min(pts.length - 1, i + 1)];
      const tx = next.x - prev.x, ty = next.y - prev.y;
      const m = Math.hypot(tx, ty) || 1;
      const px = -ty / m, py = tx / m;
      const offset = Math.sin(wigglePhase + i * 0.18) * wiggleAmp;
      const x = pts[i].x + px * offset;
      const y = pts[i].y + py * offset;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.restore();
    return true;
  }

  // ── Wind area-of-effect flash ───────────────────────────────────────────
  // Draws the conic AOE outline so the user can see exactly which region
  // the wind cast considers.  Quick fade-in, brief hold, then fades out.

  private _drawWindAoe(
    ctx: CanvasRenderingContext2D,
    entry: DissolveEntry,
    elapsed: number,
  ): boolean {
    if (elapsed >= WIND_AOE_TOTAL_MS) return false;
    const cone = entry.cone;
    if (!cone) return false;
    const t = elapsed / WIND_AOE_TOTAL_MS;
    // Fade-in for first 20%, hold 60%, fade-out last 20%
    const alpha =
      t < 0.2 ? (t / 0.2) * 0.55 :
      t < 0.8 ? 0.55 :
      Math.max(0, 0.55 * (1 - (t - 0.8) / 0.2));

    // Cone vertices: apex + two outer rays
    const apexX = cone.apexX, apexY = cone.apexY;
    const dx = cone.dirX, dy = cone.dirY;
    const len = cone.length;
    const ang = cone.halfAngle;
    const cA = Math.cos(ang), sA = Math.sin(ang);
    // Rotate (dx,dy) by ±halfAngle
    const r1x = dx * cA - dy * sA, r1y = dx * sA + dy * cA;
    const r2x = dx * cA + dy * sA, r2y = -dx * sA + dy * cA;
    const tipX = apexX + dx * len, tipY = apexY + dy * len;
    const ax1 = apexX + r1x * len, ay1 = apexY + r1y * len;
    const ax2 = apexX + r2x * len, ay2 = apexY + r2y * len;

    ctx.save();
    ctx.globalAlpha = alpha;
    // Fill: very faint cool blue
    ctx.fillStyle = 'rgba(150, 200, 255, 0.10)';
    ctx.beginPath();
    ctx.moveTo(apexX, apexY);
    ctx.lineTo(ax1, ay1);
    // Arc the far edge so it looks like a wedge of wind, not a flat triangle
    ctx.quadraticCurveTo(tipX, tipY, ax2, ay2);
    ctx.closePath();
    ctx.fill();
    // Outline
    ctx.strokeStyle = 'rgba(180, 220, 255, 0.85)';
    ctx.shadowColor = 'rgba(150, 200, 255, 0.7)';
    ctx.shadowBlur = 6;
    ctx.lineWidth = 1.6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.restore();
    return true;
  }

  // ── Wind resist (vibrating-in-place) ────────────────────────────────────
  // Borderline strokes that ALMOST qualify as stray bits "resist" the
  // wind by jittering in place rather than flying off.  Renders the
  // stroke at offset positions per frame; the underlying ink stroke
  // remains on the page.  Damped vibration: stronger early, settles late.

  private _drawWindResist(
    ctx: CanvasRenderingContext2D,
    entry: DissolveEntry,
    elapsed: number,
  ): boolean {
    if (elapsed >= WIND_RESIST_TOTAL_MS) return false;
    const t = elapsed / WIND_RESIST_TOTAL_MS;
    const damp = Math.max(0, 1 - t);                       // 1 → 0
    const vibAmp = damp * 1.8;                             // px peak displacement
    const phase = entry.phase ?? 0;
    // Offset perpendicular to wind direction, oscillating fast
    const dirX = entry.windDx ?? 1, dirY = entry.windDy ?? 0;
    const perpX = -dirY, perpY = dirX;
    const wig = Math.sin(elapsed * 0.045 + phase) * vibAmp;
    const dx = perpX * wig + (Math.cos(elapsed * 0.038 + phase) * vibAmp * 0.4);
    const dy = perpY * wig + (Math.sin(elapsed * 0.052 + phase) * vibAmp * 0.4);

    const pts = entry.points;
    if (pts.length < 1) return true;
    ctx.save();
    ctx.globalAlpha = damp * 0.55;
    ctx.translate(dx, dy);
    ctx.strokeStyle = 'rgba(180, 220, 255, 0.9)';
    ctx.shadowColor = 'rgba(150, 200, 255, 0.5)';
    ctx.shadowBlur = 3;
    ctx.lineWidth = 1.6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    if (pts.length === 1) {
      ctx.arc(pts[0].x, pts[0].y, 1.4, 0, Math.PI * 2);
    } else {
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    }
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.restore();
    return true;
  }

  // ── Red Flash ──────────────────────────────────────────────────────────

  private _drawRedFlash(
    ctx: CanvasRenderingContext2D,
    entry: DissolveEntry,
    elapsed: number,
  ): boolean {
    if (elapsed >= RED_FLASH_TOTAL_MS) return false;

    let alpha: number;
    let intensity: number;  // controls glow radius

    if (elapsed < RED_FLASH_PEAK_MS) {
      // Ramp up to peak
      const t = elapsed / RED_FLASH_PEAK_MS;
      alpha = easeOutCubic(t) * 0.9;
      intensity = t;
    } else if (elapsed < RED_FLASH_PEAK_MS + RED_FLASH_HOLD_MS) {
      // Hold at peak
      alpha = 0.9;
      intensity = 1;
    } else {
      // Fade out
      const t = (elapsed - RED_FLASH_PEAK_MS - RED_FLASH_HOLD_MS) / RED_FLASH_FADE_MS;
      alpha = 0.9 * (1 - easeOutCubic(t));
      intensity = 1 - t;
    }

    const pts = entry.points;
    if (pts.length < 2) return elapsed < RED_FLASH_TOTAL_MS;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = '#cc2020';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (intensity > 0.3) {
      ctx.shadowColor = '#ff3030';
      ctx.shadowBlur = 8 * intensity;
    }

    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      ctx.lineTo(pts[i].x, pts[i].y);
    }
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.restore();

    return true;
  }

  // ── Plain fade-out (used for dialog / region strokes) ─────────────────

  private _drawFade(
    ctx: CanvasRenderingContext2D,
    entry: DissolveEntry,
    elapsed: number,
  ): boolean {
    if (elapsed >= FADE_TOTAL_MS) return false;
    const t = elapsed / FADE_TOTAL_MS;
    const alpha = (1 - easeOutCubic(t)) * 0.85;
    const pts = entry.points;
    if (pts.length < 2) return true;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = '#3a2010';
    ctx.lineWidth = 1.6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      ctx.lineTo(pts[i].x, pts[i].y);
    }
    ctx.stroke();
    ctx.restore();
    return true;
  }

  // ── Smoke Dissolve ─────────────────────────────────────────────────────

  private _spawnSmokeParticles(pts: Array<{ x: number; y: number }>): SmokeParticle[] {
    // Compute total stroke length
    let totalLen = 0;
    for (let i = 1; i < pts.length; i++) {
      totalLen += Math.sqrt(
        (pts[i].x - pts[i - 1].x) ** 2 + (pts[i].y - pts[i - 1].y) ** 2,
      );
    }

    const count = Math.max(8, Math.round(totalLen * PARTICLES_PER_PX));
    const rng = mulberry32(Math.round(pts[0]?.x ?? 0) * 137 + Math.round(pts[0]?.y ?? 0) * 251);
    const particles: SmokeParticle[] = [];

    for (let i = 0; i < count; i++) {
      // Pick a random position along the stroke
      const t = rng();
      const targetDist = t * totalLen;
      let accum = 0;
      let px = pts[0]?.x ?? 0;
      let py = pts[0]?.y ?? 0;
      for (let j = 1; j < pts.length; j++) {
        const segLen = Math.sqrt(
          (pts[j].x - pts[j - 1].x) ** 2 + (pts[j].y - pts[j - 1].y) ** 2,
        );
        if (accum + segLen >= targetDist) {
          const f = segLen > 0 ? (targetDist - accum) / segLen : 0;
          px = pts[j - 1].x + (pts[j].x - pts[j - 1].x) * f;
          py = pts[j - 1].y + (pts[j].y - pts[j - 1].y) * f;
          break;
        }
        accum += segLen;
      }

      // Slight random offset from stroke line
      px += (rng() - 0.5) * 6;
      py += (rng() - 0.5) * 6;

      // Upward-ish drift with horizontal swirl
      const angle = -Math.PI / 2 + (rng() - 0.5) * 1.2;  // mostly upward
      const speed = 30 + rng() * 50;  // px per second
      particles.push({
        x: px,
        y: py,
        vx: Math.cos(angle) * speed + (rng() - 0.5) * 20,
        vy: Math.sin(angle) * speed - 10, // slight upward bias
        size: 1.5 + rng() * 3,
        life: 0,
        seed: rng(),
      });
    }

    return particles;
  }

  private _drawSmoke(
    ctx: CanvasRenderingContext2D,
    entry: DissolveEntry,
    elapsed: number,
  ): boolean {
    if (elapsed >= SMOKE_TOTAL_MS) return false;

    const particles = entry.particles;
    if (!particles || particles.length === 0) return false;

    // During spawn phase: draw the fading original stroke
    if (elapsed < SMOKE_SPAWN_MS) {
      const strokeAlpha = 1 - elapsed / SMOKE_SPAWN_MS;
      const pts = entry.points;
      if (pts.length >= 2) {
        ctx.save();
        ctx.globalAlpha = strokeAlpha * 0.5;
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) {
          ctx.lineTo(pts[i].x, pts[i].y);
        }
        ctx.stroke();
        ctx.restore();
      }
    }

    // Draw particles
    const dt = elapsed / 1000; // seconds
    ctx.save();
    for (const p of particles) {
      // Stagger spawn: each particle activates at a random fraction of spawn phase
      const spawnT = p.seed * SMOKE_SPAWN_MS;
      if (elapsed < spawnT) continue;

      const particleAge = (elapsed - spawnT) / 1000;
      const lifeFrac = Math.min(1, particleAge / (SMOKE_DRIFT_MS / 1000));

      // Position: base + velocity * time + slight sinusoidal swirl
      const swirlX = Math.sin(particleAge * 4 + p.seed * 10) * 8;
      const swirlY = Math.cos(particleAge * 3 + p.seed * 7) * 4;
      const cx = p.x + p.vx * particleAge + swirlX;
      const cy = p.y + p.vy * particleAge + swirlY;

      // Size grows slightly then shrinks
      const sizeMul = lifeFrac < 0.3
        ? 1 + lifeFrac / 0.3 * 0.8
        : 1.8 * (1 - (lifeFrac - 0.3) / 0.7);
      const r = p.size * Math.max(0, sizeMul);

      // Alpha: fade out
      const alpha = (1 - easeInQuad(lifeFrac)) * 0.7;

      if (alpha < 0.01 || r < 0.3) continue;

      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#1a1a1a';

      // Soft circle (multiple overlapping translucent circles for smoky feel)
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();

      // Outer halo for smoky softness
      if (r > 2) {
        ctx.globalAlpha = alpha * 0.3;
        ctx.beginPath();
        ctx.arc(cx, cy, r * 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();

    return true;
  }
}
